import React from 'react';
import { ChevronRight, Loader2, ShieldAlert } from 'lucide-react';
import {
  PA_ROUTINE_EVENT_LABELS,
  PA_ROUTINE_EVENT_TYPES,
  type PaRoutine,
  type PaRoutineRun,
  type PaRoutineRunDetail,
} from '@easyflow/shared';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@easyflow/ui-frontend/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  formatRoutineDateTime,
  ROUTINE_SOURCE_LABELS,
  RoutineStatusBadge,
} from './routinePresentation';
import { WEEKDAYS, type ScheduleDraft } from './routineSchedule';

interface RoutinesDialogsProps {
  logRoutine: PaRoutine | null;
  runs: PaRoutineRun[];
  runsLoading: boolean;
  runsError: string | null;
  runDetail: PaRoutineRunDetail | null;
  runDetailLoading: boolean;
  onCloseLogs: () => void;
  onClearRunDetail: () => void;
  onOpenRunDetail: (routine: PaRoutine, run: PaRoutineRun) => void;
  scheduleRoutine: PaRoutine | null;
  scheduleDraft: ScheduleDraft | null;
  scheduleSaving: boolean;
  onScheduleDraftChange: (draft: ScheduleDraft) => void;
  onCloseSchedule: () => void;
  onScheduleSave: () => void;
  deleteTarget: PaRoutine | null;
  deleteSending: boolean;
  onCloseDelete: () => void;
  onDelete: () => void;
  confirmRun: { routine: PaRoutine; run: PaRoutineRunDetail } | null;
  confirmSending: boolean;
  onCloseConfirm: () => void;
  onConfirmDecision: (decision: 'approve' | 'reject') => void;
}

const dialogBackdrop = (
  <DialogPortal>
    <DialogOverlay className="z-[10030]" />
  </DialogPortal>
);

export function RoutinesDialogs(props: RoutinesDialogsProps): React.ReactElement {
  const {
    logRoutine, runs, runsLoading, runsError, runDetail, runDetailLoading,
    onCloseLogs, onClearRunDetail, onOpenRunDetail,
    scheduleRoutine, scheduleDraft, scheduleSaving, onScheduleDraftChange,
    onCloseSchedule, onScheduleSave,
    deleteTarget, deleteSending, onCloseDelete, onDelete,
    confirmRun, confirmSending, onCloseConfirm, onConfirmDecision,
  } = props;

  return (
    <>
      <Dialog open={logRoutine !== null} onOpenChange={open => { if (!open) onCloseLogs(); }}>
        {dialogBackdrop}
        <DialogContent className="z-[10040] max-w-2xl sm:flex sm:h-[80vh] sm:flex-col" onEscapeKeyDown={event => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Registro esecuzioni</DialogTitle>
            <DialogDescription>{logRoutine?.title}</DialogDescription>
          </DialogHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            {runsError && <p role="alert" className="text-sm text-destructive">{runsError}</p>}
            {runDetail ? (
              <>
                <Button variant="ghost" size="sm" className="self-start" onClick={onClearRunDetail}>← Tutte le esecuzioni</Button>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <RoutineStatusBadge status={runDetail.status} />
                  <span className="text-muted-foreground">{ROUTINE_SOURCE_LABELS[runDetail.trigger_source]} · {formatRoutineDateTime(runDetail.started_at)}</span>
                </div>
                {runDetail.error_message && <p className="text-sm text-destructive">{runDetail.error_message}</p>}
                <ScrollArea className="min-h-0 flex-1 pr-3">
                  <div className="space-y-2">
                    {runDetail.node_logs.length === 0 && <p className="text-sm text-muted-foreground">Nessun passo registrato.</p>}
                    {runDetail.node_logs.map((step, index) => (
                      <div key={`${step.nodeId}-${index}`} className="space-y-1 rounded-md border p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">{step.nodeName}</span>
                          <RoutineStatusBadge status={step.status === 'success' ? 'completed' : step.status} />
                        </div>
                        {typeof step.durationMs === 'number' && <p className="text-xs text-muted-foreground">{(step.durationMs / 1000).toFixed(1)}s</p>}
                        {step.error && <p className="text-xs text-destructive">{step.error}</p>}
                        {step.output !== undefined && step.output !== null && (
                          <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 text-xs">
                            {typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </>
            ) : runsLoading || runDetailLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : runs.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Questa routine non è ancora mai stata eseguita.</p>
            ) : (
              <ScrollArea className="min-h-0 flex-1 pr-3">
                <div className="space-y-2">
                  {runs.map(run => (
                    <button key={run.id} type="button" className="flex w-full items-center justify-between gap-2 rounded-md border p-3 text-left transition-colors hover:bg-accent/50" onClick={() => { if (logRoutine) onOpenRunDetail(logRoutine, run); }}>
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <RoutineStatusBadge status={run.status} />
                          <span className="text-xs text-muted-foreground">{ROUTINE_SOURCE_LABELS[run.trigger_source]}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatRoutineDateTime(run.started_at)}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={scheduleRoutine !== null} onOpenChange={open => { if (!open) onCloseSchedule(); }}>
        {dialogBackdrop}
        <DialogContent className="z-[10040] max-w-md" onEscapeKeyDown={event => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Cambia pianificazione</DialogTitle>
            <DialogDescription>{scheduleRoutine?.title}</DialogDescription>
          </DialogHeader>
          {scheduleDraft && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Quando deve scattare?</Label>
                <Select value={scheduleDraft.kind} onValueChange={value => onScheduleDraftChange({ ...scheduleDraft, kind: value === 'event' ? 'event' : 'time' })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="z-[10050]">
                    <SelectItem value="time">A orario</SelectItem>
                    <SelectItem value="event">Quando succede qualcosa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {scheduleDraft.kind === 'event' ? (
                <div className="space-y-2">
                  <Label>Evento</Label>
                  <Select value={scheduleDraft.eventType} onValueChange={value => {
                    const eventType = PA_ROUTINE_EVENT_TYPES.find(item => item === value);
                    if (eventType) onScheduleDraftChange({ ...scheduleDraft, eventType });
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[10050]">
                      {PA_ROUTINE_EVENT_TYPES.map(eventType => <SelectItem key={eventType} value={eventType}>{PA_ROUTINE_EVENT_LABELS[eventType]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Ricorrenza</Label>
                    <Select value={scheduleDraft.frequency} onValueChange={value => {
                      const frequency = (['daily', 'weekdays', 'weekly', 'monthly', 'custom'] as const).find(item => item === value);
                      if (frequency) onScheduleDraftChange({ ...scheduleDraft, frequency });
                    }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent className="z-[10050]">
                        <SelectItem value="daily">Ogni giorno</SelectItem>
                        <SelectItem value="weekdays">Giorni feriali</SelectItem>
                        <SelectItem value="weekly">Ogni settimana</SelectItem>
                        <SelectItem value="monthly">Ogni mese</SelectItem>
                        <SelectItem value="custom">Avanzata</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {scheduleDraft.frequency === 'weekly' && (
                    <div className="space-y-2">
                      <Label>Giorno della settimana</Label>
                      <Select value={scheduleDraft.weekday} onValueChange={weekday => onScheduleDraftChange({ ...scheduleDraft, weekday })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent className="z-[10050]">{WEEKDAYS.map(day => <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {scheduleDraft.frequency === 'monthly' && (
                    <div className="space-y-2">
                      <Label>Giorno del mese</Label>
                      <Input type="number" min={1} max={31} value={scheduleDraft.monthday} onChange={event => onScheduleDraftChange({ ...scheduleDraft, monthday: event.target.value })} />
                    </div>
                  )}
                  {scheduleDraft.frequency === 'custom' ? (
                    <div className="space-y-2">
                      <Label>Espressione avanzata</Label>
                      <Input value={scheduleDraft.customCron} onChange={event => onScheduleDraftChange({ ...scheduleDraft, customCron: event.target.value })} placeholder="es. 0 9 * * 1-5" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Orario</Label>
                      <Input type="time" value={scheduleDraft.time} onChange={event => onScheduleDraftChange({ ...scheduleDraft, time: event.target.value })} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={onCloseSchedule}>Annulla</Button>
            <Button disabled={scheduleSaving} onClick={onScheduleSave}>
              {scheduleSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget !== null} onOpenChange={open => { if (!open && !deleteSending) onCloseDelete(); }}>
        <AlertDialogPortal>
          <AlertDialogOverlay className="z-[10030]" />
        </AlertDialogPortal>
        <AlertDialogContent className="z-[10040]" onEscapeKeyDown={event => event.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa routine?</AlertDialogTitle>
            <AlertDialogDescription>“{deleteTarget?.title}” e il suo registro esecuzioni verranno eliminati definitivamente.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSending}>Annulla</AlertDialogCancel>
            <AlertDialogAction disabled={deleteSending} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={event => { event.preventDefault(); onDelete(); }}>
              {deleteSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={confirmRun !== null} onOpenChange={open => { if (!open && !confirmSending) onCloseConfirm(); }}>
        {dialogBackdrop}
        <DialogContent className="z-[10040] max-w-md" onEscapeKeyDown={event => event.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-warning" />Conferma prima di procedere</DialogTitle>
            <DialogDescription>{confirmRun?.routine.title}</DialogDescription>
          </DialogHeader>
          {confirmRun?.run.pending_confirmation && (
            <div className="space-y-2 text-sm">
              <p>{confirmRun.run.pending_confirmation.message}</p>
              <p className="text-muted-foreground">
                Passo: {confirmRun.run.pending_confirmation.step_label} · Elementi coinvolti:{' '}
                <span className="font-semibold text-foreground">{confirmRun.run.pending_confirmation.count}</span>
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={confirmSending} onClick={() => onConfirmDecision('reject')}>Annulla esecuzione</Button>
            <Button disabled={confirmSending} onClick={() => onConfirmDecision('approve')}>
              {confirmSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Conferma e prosegui
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}